import React, { useState, useRef, useEffect, ReactElement } from 'react';
import { Minus, Plus } from 'lucide-react';
import Input from './Input';
import { Button } from './Button';
import { twMerge } from 'tailwind-merge';

interface InputNumberProps {
	min?: number;
	max?: number;
	placeholder?: string;
	onChange?: (value: number) => void;
	label?: string | ReactElement;
	className?: string;
	defaultValue?: number;
	value?: number;
}

const InputNumber = ({ min, max, placeholder, label, onChange, className, defaultValue, value: propValue }: InputNumberProps) => {
	const [value, setValue] = useState(propValue ?? defaultValue ?? 0);
	const incrementInterval = useRef<ReturnType<typeof setInterval> | null>(null);
	const decrementInterval = useRef<ReturnType<typeof setInterval> | null>(null);
	const delayTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		if (propValue !== undefined) {
			setValue(propValue);
		}
	}, [propValue]);

	const stopAll = () => {
		if (delayTimeout.current) clearTimeout(delayTimeout.current);
		if (incrementInterval.current) clearInterval(incrementInterval.current);
		if (decrementInterval.current) clearInterval(decrementInterval.current);
	};

	const incrementValue = () => {
		if (max !== undefined && value >= max) {
			stopAll();
			return;
		}
		setValue((prev) => {
			const newValue = Math.min(max ?? Infinity, prev + 1);
			onChange?.(newValue);
			return newValue;
		});
	};

	const decrementValue = () => {
		if (min !== undefined && value <= min) {
			stopAll();
			return;
		}
		setValue((prev) => {
			const newValue = Math.max(min ?? -Infinity, prev - 1);
			onChange?.(newValue);
			return newValue;
		});
	};

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		let newValue = Number(e.target.value);
		if (min !== undefined && newValue < min) newValue = min;
		if (max !== undefined && newValue > max) newValue = max;
		setValue(newValue);
		onChange?.(newValue);
	};

	const startIncrement = () => {
		stopAll();
		incrementValue();
		delayTimeout.current = setTimeout(() => {
			incrementInterval.current = setInterval(incrementValue, 50);
		}, 500);
	};

	const startDecrement = () => {
		stopAll();
		decrementValue();
		delayTimeout.current = setTimeout(() => {
			decrementInterval.current = setInterval(decrementValue, 50);
		}, 500);
	};

	const defaultButtonClasses =
		'p-0 rounded-sm w-9 border-l rounded-none text-gray-500 hover:bg-transparent hover:text-gray-900 border-gray-200';

	const controlsRef = useRef<HTMLDivElement>(null);
	const [controlsWidth, setControlsWidth] = useState(72);

	useEffect(() => {
		const el = controlsRef.current;
		if (!el) return;

		const update = () => setControlsWidth(el.offsetWidth);
		update();

		const observer = new ResizeObserver(update);
		observer.observe(el);
		return () => observer.disconnect();
	}, [label]);

	return (
		<div className="relative group">
			<Input
				min={min}
				max={max}
				placeholder={placeholder}
				type="number"
				className={twMerge(
					'[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none min-w-[150px]',
					className
				)}
				style={{ paddingRight: controlsWidth }}
				value={value}
				onChange={handleInputChange}
			/>

			<div ref={controlsRef} className='absolute right-0 top-1/2 -translate-y-1/2 flex flex-row h-full'>
				{label && (
					<span className="text-gray-500 font-normal text-xs select-none pointer-events-none px-3 flex items-center whitespace-nowrap">
						{label}
					</span>
				)}
				<Button
					variant={'ghost'}
					className={twMerge(defaultButtonClasses)}
					onMouseDown={startIncrement}
					onMouseUp={stopAll}
					onMouseLeave={stopAll}
				>
					<Plus />
				</Button>
				<Button
					variant={'ghost'}
					className={twMerge(defaultButtonClasses)}
					onMouseDown={startDecrement}
					onMouseUp={stopAll}
					onMouseLeave={stopAll}
				>
					<Minus />
				</Button>
			</div>
		</div>
	);
};

export default InputNumber;
